/**
 * Amplifier profiles -- group 03 (British classics and Japanese Class A).
 *
 * Six of these eight are integrated amplifiers, which is what these brands
 * actually build, so the differentiation has to come from the electrical
 * design rather than the form factor: how much global feedback is applied,
 * whether the output stage ever leaves Class A, how stiff the source impedance
 * is, and what the transformer or supply does at the frequency extremes. Those
 * are the parameters the kit turns into different DSP, so they are the ones
 * authored here.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_03: AmpProfile[] = [
  makeAmp({
    mfr: 'Exposure',
    model: '3510 Power Amplifier',
    price: usd(
      3795,
      'APPROXIMATE',
      2022,
      'Approximate US retail; Exposure quotes in GBP and US pricing varies by dealer.',
    ),
    year: 2021,
    country: 'United Kingdom',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Complementary bipolar output pair per channel, discrete regulated supplies for the small-signal stages',
    power: { watts8: 110, watts4: 190, confidence: 'ESTIMATED' },
    gainDb: 29,
    thdPercent: 0.01,
    snrDb: 100,
    bandwidth: [5, 80000],
    dampingFactor: 90,
    feedbackDb: 24,
    crosstalkDb: -85,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.45, crossoverWidth: 0.004, headroomDb: 10, asymmetry: 0.18, oversample: 4 },
    dynamics: { sagDb: 0.7, sagTimeMs: 70, recoveryMs: 160, slewRateVus: 55, headroomDb: 1.6 },
    clipping: { kind: 'asymmetric', kneeDb: 2.2, stickiness: 0.1 },
    confidence: 'ESTIMATED',
    accent: '#5b6b7a',
    tags: ['british', 'short-signal-path', 'moderate-feedback'],
    notes:
      'A deliberately short signal path with a single output pair per channel and only moderate global feedback, so the residual is a classic Class AB pattern: a small crossover region that leaves a fixed low-level notch, and higher odd orders that do not fall away as fast as a Class A design. The 0.09 ohm source impedance gives a damping factor near 90, which is stiff enough that loudspeaker impedance modulation of the response stays under about 0.2 dB on an 8 ohm nominal load but becomes visible on a 3 ohm minimum. Supply sag is set higher than the large-transformer designs in this group because the reservoir is sized for a single output pair.',
    provenanceNote:
      'Rated power, form factor and the single-output-pair topology are from Exposure published material. THD, signal-to-noise, damping factor, feedback depth and bandwidth are ESTIMATED from the product class; Exposure publishes an abbreviated specification only.',
  }),

  makeAmp({
    mfr: 'Sugden',
    model: 'A21SE',
    price: usd(
      3995,
      'APPROXIMATE',
      2018,
      'Approximate US retail; Sugden quotes in GBP and the US price varies with exchange rate.',
    ),
    year: 2014,
    country: 'United Kingdom',
    topology: 'solid-state-a',
    form: 'integrated',
    devices: 'Single-ended-biased pure Class A output stage, one device pair per channel, no output relay in circuit',
    power: { watts8: 30, watts4: 33, confidence: 'ESTIMATED' },
    gainDb: 34,
    thdPercent: 0.05,
    snrDb: 92,
    bandwidth: [8, 60000],
    dampingFactor: 45,
    feedbackDb: 8,
    crosstalkDb: -75,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.6, asymmetry: 0.6, bias: 0.75, headroomDb: 9, oversample: 4 },
    dynamics: { sagDb: 0.6, sagTimeMs: 90, recoveryMs: 200, thermalDb: 0.5, thermalTimeMs: 9000, slewRateVus: 30, headroomDb: 0.7 },
    clipping: { kind: 'asymmetric', kneeDb: 5, stickiness: 0.15 },
    confidence: 'ESTIMATED',
    accent: '#a8703c',
    tags: ['pure-class-a', 'low-feedback', 'low-power'],
    notes:
      'The output devices never switch off, so there is no crossover region and no fixed low-level notch at all; instead distortion is almost entirely second harmonic and grows steadily with drive, which is the opposite level behaviour to the Class AB entries here. Low feedback leaves a 0.18 ohm source impedance, so the loudspeaker impedance curve prints onto the frequency response at around half a dB on a typical two-way. Thirty watts also means the clipping model matters: on an 86 dB speaker this amplifier reaches its soft asymmetric knee at realistic listening levels, and thermal drift is modelled higher than the other solid-state entries because the heatsinks run near their design limit continuously.',
    provenanceNote:
      'Pure Class A operation and the single-pair output topology are from Sugden published material. Rated power, THD, signal-to-noise, damping factor and bandwidth are ESTIMATED from the product class; Sugden publishes a short specification and the exact power figure varies between revisions of the A21.',
  }),

  makeAmp({
    mfr: 'Arcam',
    model: 'A49',
    price: usd(5999, 'ORIGINAL_RETAIL', 2014, 'US retail as launched; the A49 is discontinued.'),
    year: 2014,
    country: 'United Kingdom',
    topology: 'class-g',
    form: 'integrated',
    devices: 'Class G output: Class A operation on the low rail, commutating to a second higher-voltage rail pair on peaks',
    power: { watts8: 200, watts4: 400, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 37,
    thdPercent: 0.002,
    snrDb: 105,
    bandwidth: [5, 60000],
    dampingFactor: 200,
    feedbackDb: 34,
    crosstalkDb: -95,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.3, crossoverWidth: 0.002, headroomDb: 10, asymmetry: 0.12, oversample: 4 },
    dynamics: { sagDb: 0.3, sagTimeMs: 50, recoveryMs: 130, slewRateVus: 70, headroomDb: 2.4 },
    clipping: { kind: 'asymmetric', kneeDb: 1.8, stickiness: 0.06 },
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4f8fbd',
    tags: ['class-g', 'rail-switching', 'high-power'],
    notes:
      'Rail commutation is the whole signature. Below roughly a tenth of rated power the output stage sits in Class A on the low rail and the residual is small and low order; above that the amplifier switches to the higher rail and the transition itself contributes a brief discontinuity that behaves like a very narrow crossover event rather than the wide one a Class B stage has. That is modelled with a narrow crossover width and a 10 dB headroom parameter placing the transition about a decade below rated output. With 200 watts, a damping factor near 200 and deep global feedback, load interaction and supply sag are both close to negligible, which makes it the most electrically transparent of the British entries here.',
    provenanceNote:
      'Class G topology, rated power into 8 and 4 ohms, THD and signal-to-noise are from Arcam published specifications. Feedback depth, the rail-transition level and the dynamic model are DERIVED or ESTIMATED from that topology; Arcam does not publish a rail-switching threshold.',
  }),

  makeAmp({
    mfr: 'Creek Audio',
    model: 'Evolution 100A',
    price: usd(
      2195,
      'APPROXIMATE',
      2015,
      'Approximate US retail at launch; Creek US pricing varied by distributor.',
    ),
    year: 2014,
    country: 'United Kingdom',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'Three complementary bipolar output pairs per channel from a single large toroidal supply',
    power: { watts8: 110, watts4: 170, confidence: 'ESTIMATED' },
    gainDb: 36,
    thdPercent: 0.005,
    snrDb: 103,
    bandwidth: [10, 50000],
    dampingFactor: 150,
    feedbackDb: 32,
    crosstalkDb: -90,
    harmonicShape: HARMONIC_SHAPES.highFeedback,
    nonlinear: { kind: 'class-b-crossover', amount: 0.35, crossoverWidth: 0.0025, headroomDb: 11, asymmetry: 0.1, oversample: 4 },
    dynamics: { sagDb: 0.45, sagTimeMs: 60, recoveryMs: 140, slewRateVus: 60, headroomDb: 1.9 },
    clipping: { kind: 'asymmetric', kneeDb: 2.0, stickiness: 0.08 },
    confidence: 'ESTIMATED',
    accent: '#6d8f6a',
    tags: ['british', 'high-feedback', 'multiple-output-pairs'],
    notes:
      'Same nominal power and the same class as the Exposure entry, but a different design compromise, and the profile is built to make that audible. Three output pairs per channel share the current, so each device stays further from its nonlinear region and the crossover width is narrower; substantially deeper global feedback then pushes the absolute distortion floor down by about 6 dB but redistributes what is left toward higher orders, which is why this entry uses the high-feedback harmonic distribution rather than the Class AB one. Damping factor near 150 and a large single supply also make it stiffer into difficult loads than the short-signal-path design.',
    provenanceNote:
      'Rated power, integrated form and the multiple-output-pair layout are from Creek published material. THD, signal-to-noise, damping factor, feedback depth and bandwidth are ESTIMATED from the product class; the harmonic distribution is a modelled consequence of the stated feedback approach, not a measurement.',
  }),

  makeAmp({
    mfr: 'Rega',
    model: 'Aethos',
    price: usd(
      3995,
      'APPROXIMATE',
      2019,
      'Approximate US retail at launch; Rega quotes in GBP and US pricing has moved since.',
    ),
    year: 2019,
    country: 'United Kingdom',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'Class A voltage amplifier stage driving a complementary bipolar output pair set, dual mono rails',
    power: { watts8: 125, watts4: 200, confidence: 'ESTIMATED' },
    gainDb: 33,
    thdPercent: 0.008,
    snrDb: 98,
    bandwidth: [10, 55000],
    dampingFactor: 120,
    feedbackDb: 18,
    crosstalkDb: -82,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.5, crossoverWidth: 0.005, headroomDb: 9, asymmetry: 0.32, oversample: 4 },
    dynamics: { sagDb: 0.55, sagTimeMs: 80, recoveryMs: 180, slewRateVus: 45, headroomDb: 1.4 },
    clipping: { kind: 'asymmetric', kneeDb: 3.2, stickiness: 0.14 },
    confidence: 'ESTIMATED',
    accent: '#c2555a',
    tags: ['british', 'class-a-driver', 'low-feedback', 'no-tone-controls'],
    notes:
      'The distinguishing choice here is the amount of feedback, not the class: the voltage amplifier stage runs in Class A and global feedback is kept low, which leaves a noticeably asymmetric transfer curve and a residual weighted toward the second harmonic even though the output stage itself is AB. That is why this profile carries a wider crossover width and a much higher asymmetry term than the high-feedback entry of similar power. Bandwidth is also deliberately bounded rather than extended, so the modelled response is a clean first-order roll-off starting well outside the audio band rather than the near-DC-to-daylight shape of a heavily compensated design.',
    provenanceNote:
      'Rated power, integrated form and the Class A driver stage are from Rega published material. THD, signal-to-noise, damping factor, feedback depth and bandwidth are ESTIMATED from the product class; Rega publishes only a partial specification.',
  }),

  makeAmp({
    mfr: 'Quad',
    model: 'II-Eighty',
    price: usd(6999, 'APPROXIMATE', 2018, 'Approximate US retail per pair of monoblocks.'),
    year: 2013,
    country: 'United Kingdom',
    topology: 'tube-push-pull',
    form: 'monoblock',
    devices: 'KT88 push-pull output pair with cathode-coupled feedback windings on the output transformer',
    power: { watts8: 80, watts4: 80, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.5,
    snrDb: 88,
    bandwidth: [12, 40000],
    outputImpedanceOhms: 1.0,
    feedbackDb: 14,
    outputTransformer: true,
    transformerLfHz: 16,
    transformerHfHz: 36000,
    crosstalkDb: -62,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    nonlinear: { kind: 'pentode', amount: 0.55, asymmetry: 0.22, bias: 0.2, headroomDb: 11, oversample: 4 },
    dynamics: { sagDb: 0.9, sagTimeMs: 160, recoveryMs: 380, slewRateVus: 20, headroomDb: 1.3 },
    clipping: { kind: 'soft', kneeDb: 6, stickiness: 0.3 },
    confidence: 'ESTIMATED',
    accent: '#8f7fbf',
    tags: ['kt88', 'cathode-feedback', 'push-pull', 'monoblock'],
    notes:
      'Push-pull operation cancels most of the even-order distortion, so unlike a single-ended triode this amplifier is third-harmonic dominant, and the cathode-coupled feedback windings apply local feedback around the output transformer rather than only around the whole amplifier. The result modelled here is a tube amplifier with an unusually low 1 ohm source impedance for its type, giving a damping factor near 8 instead of the 2 to 4 typical of a low-feedback design, so loudspeaker impedance modulation is present but restrained at roughly 1 dB on a wide-swinging load. Transformer core behaviour sets the 16 Hz low-frequency corner and adds saturation-driven low-order distortion below about 40 Hz at high level.',
    provenanceNote:
      'KT88 push-pull topology, cathode feedback and monoblock form are from Quad published material. Rated power, THD, signal-to-noise, output impedance, feedback depth and transformer corner frequencies are ESTIMATED from the topology; Quad does not publish a full distortion-versus-power specification for this model.',
  }),

  makeAmp({
    mfr: 'Leben',
    model: 'CS600X',
    price: usd(
      7495,
      'APPROXIMATE',
      2020,
      'Approximate US retail; Leben is imported in small numbers and dealer pricing varies.',
    ),
    year: 2019,
    country: 'Japan',
    topology: 'tube-push-pull',
    form: 'integrated',
    devices: 'EL34 push-pull pair per channel (6L6GC and 7581A selectable by bias switch), 6CG7 driver, 12AX7 input',
    power: { watts8: 32, watts4: 28, confidence: 'ESTIMATED' },
    gainDb: 32,
    thdPercent: 1.0,
    snrDb: 84,
    bandwidth: [15, 42000],
    outputImpedanceOhms: 1.8,
    feedbackDb: 6,
    outputTransformer: true,
    transformerLfHz: 20,
    transformerHfHz: 38000,
    crosstalkDb: -58,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    nonlinear: { kind: 'pentode', amount: 0.72, asymmetry: 0.45, bias: 0.45, headroomDb: 8, oversample: 8 },
    dynamics: { sagDb: 1.5, sagTimeMs: 200, recoveryMs: 500, slewRateVus: 14, headroomDb: 1.0 },
    clipping: { kind: 'soft', kneeDb: 8.5, stickiness: 0.4 },
    confidence: 'ESTIMATED',
    accent: '#d9a441',
    tags: ['el34', 'low-feedback', 'push-pull', 'selectable-output-tubes'],
    notes:
      'The same nominal topology as the KT88 monoblock in this group and a completely different result, because the feedback is roughly 8 dB shallower and the power is a quarter as large. Push-pull cancellation is incomplete at this feedback depth, so a substantial second harmonic survives alongside the third and the asymmetry term is set much higher. The 1.8 ohm source impedance gives a damping factor near 4, which is low enough that the loudspeaker impedance curve is reproduced in the frequency response at well over a dB, and the supply sags almost a dB and a half on sustained peaks, compressing dynamics in a way the solid-state entries do not.',
    provenanceNote:
      'Push-pull EL34 topology, selectable output tube types and integrated form are from Leben published material. Rated power, THD, signal-to-noise, output impedance, feedback depth and transformer corners are ESTIMATED from the topology; Leben does not publish THD versus power.',
  }),

  makeAmp({
    mfr: 'Accuphase',
    model: 'E-800',
    price: usd(
      18500,
      'APPROXIMATE',
      2020,
      'Approximate US retail; Accuphase US pricing moves with exchange rate and dealer.',
    ),
    year: 2019,
    country: 'Japan',
    topology: 'solid-state-a',
    form: 'integrated',
    devices: 'Ten parallel push-pull bipolar devices per channel in pure Class A, current-feedback amplifier stage, AAVA gain-domain volume control',
    power: { watts8: 50, watts4: 100, watts2: 200, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 36,
    thdPercent: 0.05,
    snrDb: 110,
    bandwidth: [1, 150000],
    dampingFactor: 1000,
    feedbackDb: 28,
    crosstalkDb: -100,
    harmonicShape: HARMONIC_SHAPES.highFeedback,
    nonlinear: { kind: 'jfet-square', amount: 0.2, asymmetry: 0.18, bias: 0.35, headroomDb: 14, oversample: 4 },
    dynamics: { sagDb: 0.15, sagTimeMs: 40, recoveryMs: 110, thermalDb: 0.25, thermalTimeMs: 12000, slewRateVus: 90, headroomDb: 3.0 },
    clipping: { kind: 'asymmetric', kneeDb: 3.0, stickiness: 0.07 },
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c8b48b',
    tags: ['pure-class-a', 'current-feedback', 'aava-volume', 'high-damping'],
    notes:
      'Pure Class A like the Sugden entry, but with two decades more loop gain and ten output pairs per channel, so the two profiles sit at opposite ends of the same class. Power doubles into 4 ohms and doubles again into 2, which the model reflects as a very small sag term and a source impedance of 0.008 ohm, effectively removing loudspeaker impedance interaction from the chain. What distortion remains is 60 dB below the low-feedback designs here and is distributed toward higher orders rather than concentrated in the second harmonic, so the harmonic model uses the high-feedback distribution. Volume is handled in the current domain rather than by a resistive divider, so the noise floor does not rise as attenuation is reduced.',
    provenanceNote:
      'Rated power into 8, 4 and 2 ohms, damping factor, THD, signal-to-noise and pure Class A operation are from Accuphase published specifications. Feedback depth, slew rate and the harmonic distribution are DERIVED or ESTIMATED from that specification and the stated topology.',
  }),
];
